export type SalesInvoiceStatus =
  | 'DRAFT' | 'ISSUED' | 'PARTIALLY_PAID' | 'PAID' | 'VOIDED';

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

export const SALES_INVOICE_STATUS_COLORS: Record<SalesInvoiceStatus, ChipColor> = {
  DRAFT: 'default',
  ISSUED: 'warning',
  PARTIALLY_PAID: 'info',
  PAID: 'success',
  VOIDED: 'error',
};

// Mirrors SalesInvoiceLineDTO (IdHolder gives id)
export interface SalesInvoiceLine {
  id: string;
  salesInvoiceId: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRatePct: number;
  lineTax?: number;
  lineTotal?: number;
  /** B4-C: unit price in the document currency (unitPrice is then the BDT at the document rate). */
  foreignUnitPrice?: number | null;
  foreignAmount?: number | null;
  foreignDiscountAmount?: number | null;   // FW-2A: discount in the document currency (discountAmount is BDT)
  revenueAccountId?: string;
  segmentId?: string;
  taxCodeId?: string | null;
  /** W3-O: set when the line was built from a delivery challan (quantity is then frozen). */
  salesOrderLineId?: string | null;
  deliveryChallanLineId?: string | null;
  // ---- FW-2A: line discount (gross = quantity x unit price; lineTotal is the NET after the discount) and catalogue item ----
  grossAmount?: number | null;
  discountPct?: number | null;
  discountAmount?: number | null;
  itemId?: string | null;
  // ---- end FW-2A ----
}

// Mirrors SalesInvoiceDTO (IdHolder gives id)
export interface SalesInvoice {
  id: string;
  invoiceNumber?: string;
  /** Brought-forward opening invoice (loaded at go-live). */
  openingBalance?: boolean;
  customerId: string;
  customerName?: string;
  quotationId?: string;
  issueDate: string;
  dueDate: string;
  status: SalesInvoiceStatus;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  paidAmount: number;
  /** W3-E: total of the posted credit notes raised against this invoice. */
  creditedAmount?: number;
  // ---- B4-C: document currency (BDT unless a foreign-currency invoice) ----
  currencyCode?: string;
  exchangeRate?: number;
  foreignSubtotal?: number;
  foreignTotal?: number;
  foreignPaid?: number;
  foreignCredited?: number;
  foreignOutstanding?: number;
  // ---- end B4-C ----
  /** W3-N: credit-control WARN messages returned by Issue. */
  warnings?: string[];
  notes?: string;
  institutionId: string;
  journalEntryId?: string;
  reversalJournalEntryId?: string;
  voidReason?: string;
  overdue: boolean;
  lines: SalesInvoiceLine[];
  // ---- FW-3A: approval before Issue ----
  approvalStatus?: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED';
  approvalRequestId?: string | null;
  /** Detail read of a DRAFT only: a workflow applies, so Issue waits for an approval. */
  approvalRequired?: boolean | null;
  /** Pending only: the caller submitted the request, so only they may withdraw it. */
  approvalCanWithdraw?: boolean | null;
  // ---- end FW-3A ----
  // ---- FW-2B: a cash sale is an invoice paid in full at the moment of sale ----
  saleKind?: 'CREDIT' | 'CASH';
  walkInName?: string | null;
  walkInPhone?: string | null;
  // ---- end FW-2B ----
  // ---- P1-A tail: header project (revenue lines are tagged with it at issue); code / name on the detail read ----
  projectId?: string | null;
  projectCode?: string | null;
  projectName?: string | null;
  // ---- end P1-A tail ----
}

// Mirrors SalesInvoiceLineCreateDTO
export interface SalesInvoiceLineCreateDTO {
  description: string;
  quantity: number;
  unitPrice: number;
  taxRatePct: number;
  revenueAccountId?: string;
  segmentId?: string;
  taxCodeId?: string | null;
  /** W3-O: id of the existing challan-linked line this row updates in place. */
  existingLineId?: string;
  // ---- FW-2A: line discount (gross = quantity x unit price; lineTotal is the NET after the discount) and catalogue item ----
  discountPct?: number | null;
  discountAmount?: number | null;
  itemId?: string | null;
  // ---- end FW-2A ----
}

// Mirrors SalesInvoiceCreateDTO
export interface SalesInvoiceCreateDTO {
  customerId: string;
  quotationId?: string;
  issueDate: string;
  dueDate: string;
  notes?: string;
  institutionId: string;
  /** B4-C: omit (or BDT) for a local invoice; lines are then entered in this currency at `exchangeRate`. */
  currencyCode?: string;
  exchangeRate?: number;
  lines: SalesInvoiceLineCreateDTO[];
  /** P1-A tail: a project of this customer or one with no customer; null clears it on a draft update. */
  projectId?: string | null;
}

// Mirrors SalesInvoiceLineEditDTO
// Backend uses lineId (not id) in SalesInvoiceLineEditDTO
export interface SalesInvoiceLineEditDTO {
  lineId: string;
  unitPrice: number;
  taxRatePct: number;
  description: string;
  taxCodeId?: string | null;
  // ---- FW-2A: line discount (gross = quantity x unit price; lineTotal is the NET after the discount) and catalogue item ----
  discountPct?: number | null;
  discountAmount?: number | null;
  itemId?: string | null;
  // ---- end FW-2A ----
}

// Mirrors SalesInvoiceEditDTO + SalesInvoiceLineEditDTO
export interface SalesInvoiceEditDTO {
  editReason: string;
  issueDate: string;
  dueDate: string;
  notes?: string;
  lines: SalesInvoiceLineEditDTO[];
}

// Mirrors VoidRequestDTO
export interface VoidDTO {
  voidReason: string;
}

export interface SalesInvoicePage {
  content: SalesInvoice[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}
